import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// ===== utils =====
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }

// akceptuje: całkowite, a/b, dziesiętne (kropka/przecinek)
function parseFraction(raw) {
  const s0 = String(raw).trim();
  const s = s0.replace(",", ".");
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s, 10), 1];                       // int
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);                    // a/b
  if (m) { const den = parseInt(m[2], 10); if (den === 0) return null; return [parseInt(m[1], 10), den]; }
  const d = s.match(/^[+-]?(?:\d+|\d*\.\d+)$/);                                 // decimal
  if (d) {
    const sign = s.startsWith("-") ? -1 : 1;
    const body = s.replace(/^[+-]/, "");
    const [ip, fp = ""] = body.split(".");
    const k = fp.length;
    const p = sign * (parseInt(ip || "0", 10) * (10 ** k) + parseInt(fp || "0", 10));
    const q = 10 ** k;
    return [p, q];
  }
  return null;
}

function nCr(n, r) {
  if (r < 0 || r > n) return 0;
  r = Math.min(r, n - r);
  let num = 1, den = 1;
  for (let i = 1; i <= r; i++) { num *= (n - r + i); den *= i; }
  return Math.round(num / den);
}

// ===== stałe talii =====
const TOTAL = 52;
const SUITS = 4;
const PER_SUIT = 13;
const RED = 26, BLACK = 26;
const ACES = 4, QUEENS = 4, KINGS = 4, JACKS = 4;

// do rotacji nazw kolorów
const SUIT_LIST = [
  { key: "pik",   titleAdj: "pikowe"  }, // ♠
  { key: "kier",  titleAdj: "kier"    }, // ♥
  { key: "karo",  titleAdj: "karo"    }, // ♦
  { key: "trefl", titleAdj: "treflowe"}  // ♣
];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function pickTwoDistinct(arr) {
  const i = Math.floor(Math.random() * arr.length);
  let j = Math.floor(Math.random() * (arr.length - 1));
  if (j >= i) j++;
  return [arr[i], arr[j]];
}

// ===== warianty (część z rotacją kolorów) =====
function buildModes() {
  // rotowane: „obie [suit]”
  const BOTH_SAME_SPEC = {
    key: "BOTH_SPEC",
    mkTitle: (s) => `obie ${s.titleAdj}`,
    mkLatex: (s) => `2\\,\\text{ ${s.titleAdj}}`,
    fav: () => nCr(PER_SUIT, 2), // 13C2
    needsSuit: true
  };

  // rotowane: „1 [suitA] i 1 [suitB]”
  const ONE_AND_ONE_SPEC = {
    key: "ONE_ONE_SPEC",
    mkTitle: (a, b) => `1 ${a.key} i 1 ${b.key}`,
    mkLatex: (a, b) => `\\text{1 ${a.key} i 1 ${b.key}}`,
    fav: () => PER_SUIT * PER_SUIT, // 13*13
    needsSuitPair: true
  };

  // stałe tryby (bez rotacji)
  const FIXED = [
    { key: "RED_RED",  title: "obie czerwone", latex: "2\\,\\text{ czerwone}", fav: () => nCr(RED,2) },
    { key: "BLK_BLK",  title: "obie czarne",   latex: "2\\,\\text{ czarne}",   fav: () => nCr(BLACK,2) },

    { key: "PAIR",     title: "para",          latex: "\\text{para}",          fav: () => 13 * nCr(4,2) },

    { key: "AA",       title: "oba asy",       latex: "2\\,\\text{ asy}",      fav: () => nCr(ACES,2) },
    { key: "QQ",       title: "obie damy",     latex: "2\\,\\text{ damy}",     fav: () => nCr(QUEENS,2) },
    { key: "KK",       title: "oba króle",     latex: "2\\,\\text{ króle}",    fav: () => nCr(KINGS,2) },
    { key: "JJ",       title: "oba walety",    latex: "2\\,\\text{ walety}",   fav: () => nCr(JACKS,2) },

    { key: "EX1_A",    title: "dokładnie jeden as",   latex: "\\text{dokładnie 1 as}",  fav: () => ACES * (TOTAL - ACES) },
    { key: "GE1_A",    title: "co najmniej jeden as", latex: "\\ge 1\\,\\text{ as}",    fav: () => nCr(TOTAL,2) - nCr(TOTAL - ACES, 2) },

    { key: "SAME_CL",  title: "ten sam kolor", latex: "\\text{ten sam kolor}", fav: () => SUITS * nCr(PER_SUIT, 2) },
    { key: "DIFF_CL",  title: "różne kolory",  latex: "\\text{różne kolory}",  fav: () => nCr(TOTAL,2) - SUITS * nCr(PER_SUIT, 2) },
  ];

  return { BOTH_SAME_SPEC, ONE_AND_ONE_SPEC, FIXED };
}
const MODES_DEF = buildModes();

// ===== core =====
function generateOne() {
  // losujemy rodzaj trybu: stały / z 1 suit / z parą suitów
  const roll = Math.random();
  let mode = null;
  let title = "", latex = "";
  if (roll < 0.33) {
    // stały
    mode = pick(MODES_DEF.FIXED);
    title = mode.title;
    latex = mode.latex;
  } else if (roll < 0.66) {
    // obie [suit]
    const s = pick(SUIT_LIST);
    mode = MODES_DEF.BOTH_SAME_SPEC;
    title = mode.mkTitle(s);
    latex = mode.mkLatex(s);
  } else {
    // 1 [suit] i 1 [inny suit]
    const [a, b] = pickTwoDistinct(SUIT_LIST);
    mode = MODES_DEF.ONE_AND_ONE_SPEC;
    title = mode.mkTitle(a, b);
    latex = mode.mkLatex(a, b);
  }

  const omega = nCr(TOTAL, 2);  // 52C2 = 1326
  const fav = mode.fav();
  const [p, q] = simplify(fav, omega);
  return { omega, fav, p, q, modeKey: mode.key, modeTitle: title, modeLatex: latex };
}

export function generateTask() {
  const d = generateOne();
  return {
    type: "CardsTwoDrawsTask",
    data: d,
    uid: `cards2_${d.modeKey}_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      <div>
        <span>Ze standardowej talii 52 kart losujemy </span>
        <InlineMath math={"2"} />
        <span> karty </span>
        <b>bez zwracania</b>
        <span>. Oblicz prawdopodobieństwo, że: </span>
        <b>{data.modeTitle}</b>
        <span>.</span>
      </div>
      <div style={{ opacity: 0.8, fontSize: 14, marginTop: 6 }}>
        <span>Kolory: </span>♠ ♣ (czarne), ♥ ♦ (czerwone)
      </div>
    </div>
  );
}

// input: P(A) = …
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math={'P(A) = '} /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P(A)"
        placeholder="np. 1/17 lub 0.06"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return { correct: false, message: "Podaj ułamek (np. 3/10) lub liczbę (np. 0.3)." };
  }
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct: false, message: "Nieprawidłowy ułamek/liczba." };

  const [gp, gq] = parsed;
  const [cp, cq] = simplify(data.p, data.q);
  const [gpp, gqq] = simplify(gp, gq);

  // dokładny ułamek
  let correct = (gpp === cp && gqq === cq);

  // tolerancja dziesiętna ±0.01
  if (!correct) {
    const userVal = gp / gq;
    const correctVal = cp / cq;
    if (Math.abs(userVal - correctVal) <= 0.01 + 1e-12) correct = true;
  }
  return { correct, expected: `\\tfrac{${cp}}{${cq}}` };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`P(A) = ${dfrac(data.p, data.q)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={formatLatexPreview(userAnswer.raw, 'P(A)')} />
    </div>
  );
}

// ——— krótkie opisy do wariantów (pod treścią) ———
function ModeExplanation({ data }) {
  const { modeKey } = data;
  if (modeKey === "BOTH_SPEC")    return <div>Wybieramy 2 karty z 13 w danym kolorze ⇒ <InlineMath math={'\\binom{13}{2}'} />.</div>;
  if (modeKey === "ONE_ONE_SPEC") return <div>Wybieramy po 1 kartę z dwóch różnych kolorów ⇒ <InlineMath math={'13\\cdot13'} />.</div>;

  if (modeKey === "RED_RED")      return <div>2 z 26 czerwonych ⇒ <InlineMath math={'\\binom{26}{2}'} />.</div>;
  if (modeKey === "BLK_BLK")      return <div>2 z 26 czarnych ⇒ <InlineMath math={'\\binom{26}{2}'} />.</div>;

  if (modeKey === "PAIR")         return <div>Wybieramy rangę (13 sposobów) i 2 z 4 kart ⇒ <InlineMath math={'13\\binom{4}{2}'} />.</div>;

  if (modeKey === "AA")           return <div>2 spośród 4 asów ⇒ <InlineMath math={'\\binom{4}{2}'} />.</div>;
  if (modeKey === "QQ")           return <div>2 spośród 4 dam ⇒ <InlineMath math={'\\binom{4}{2}'} />.</div>;
  if (modeKey === "KK")           return <div>2 spośród 4 króli ⇒ <InlineMath math={'\\binom{4}{2}'} />.</div>;
  if (modeKey === "JJ")           return <div>2 spośród 4 waletów ⇒ <InlineMath math={'\\binom{4}{2}'} />.</div>;

  if (modeKey === "EX1_A")        return <div>Dokładnie 1 as: 1 z 4 oraz 1 z pozostałych 48 ⇒ <InlineMath math={'4\\cdot48'} />.</div>;
  if (modeKey === "GE1_A")        return <div>Co najmniej 1 as: wszystkie pary minus pary bez asów ⇒ <InlineMath math={'\\binom{52}{2} - \\binom{48}{2}'} />.</div>;

  if (modeKey === "SAME_CL")      return <div>Ten sam kolor: wybieramy kolor (4 sposoby) i 2 z 13 ⇒ <InlineMath math={'4\\binom{13}{2}'} />.</div>;
  if (modeKey === "DIFF_CL")      return <div>Różne kolory: wszystkie pary minus te w tym samym kolorze ⇒ <InlineMath math={'\\binom{52}{2} - 4\\binom{13}{2}'} />.</div>;

  return null;
}

// Wyjaśnienie: Ω → |A| → P(A)
export function renderExplanation({ data }) {
  const { omega, fav, modeKey } = data;
  const [simpNum, simpDen] = simplify(fav, omega);
  const tail = (simpNum === fav && simpDen === omega) ? "" : `= \\dfrac{${simpNum}}{${simpDen}}`;

  const c52_2 = nCr(52,2), c48_2 = nCr(48,2), c26_2 = nCr(26,2), c13_2 = nCr(13,2), c4_2 = nCr(4,2);

  const omegaLine = `\\Omega = \\{\\text{wszystkie 2-kartowe wybory z 52 bez zwracania}\\},\\; |\\Omega| = \\binom{52}{2} = ${c52_2}`;

  let aCountLine = "";
  switch (modeKey) {
    case "BOTH_SPEC":    aCountLine = `|\\mathrm{A}| = \\binom{13}{2} = ${c13_2}`; break;
    case "ONE_ONE_SPEC": aCountLine = `|\\mathrm{A}| = 13\\cdot13 = ${13*13}`; break;

    case "RED_RED":      aCountLine = `|\\mathrm{A}| = \\binom{26}{2} = ${c26_2}`; break;
    case "BLK_BLK":      aCountLine = `|\\mathrm{A}| = \\binom{26}{2} = ${c26_2}`; break;

    case "PAIR":         aCountLine = `|\\mathrm{A}| = 13\\binom{4}{2} = 13\\cdot${c4_2} = ${13*c4_2}`; break;

    case "AA":           aCountLine = `|\\mathrm{A}| = \\binom{4}{2} = ${c4_2}`; break;
    case "QQ":           aCountLine = `|\\mathrm{A}| = \\binom{4}{2} = ${c4_2}`; break;
    case "KK":           aCountLine = `|\\mathrm{A}| = \\binom{4}{2} = ${c4_2}`; break;
    case "JJ":           aCountLine = `|\\mathrm{A}| = \\binom{4}{2} = ${c4_2}`; break;

    case "EX1_A":        aCountLine = `|\\mathrm{A}| = 4\\cdot48 = ${4*48}`; break;
    case "GE1_A":        aCountLine = `|\\mathrm{A}| = \\binom{52}{2} - \\binom{48}{2} = ${c52_2} - ${c48_2} = ${fav}`; break;

    case "SAME_CL":      aCountLine = `|\\mathrm{A}| = 4\\binom{13}{2} = 4\\cdot${c13_2} = ${4*c13_2}`; break;
    case "DIFF_CL":      aCountLine = `|\\mathrm{A}| = \\binom{52}{2} - 4\\binom{13}{2} = ${c52_2} - 4\\cdot${c13_2} = ${fav}`; break;
  }

  const probLine  = `P(\\mathrm{A}) = \\dfrac{|\\mathrm{A}|}{|\\Omega|} = \\dfrac{${fav}}{${omega}} ${tail}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1 — przestrzeń zdarzeń</strong>
        <BlockMath math={omegaLine} />
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2 — liczymy liczność zdarzenia A</strong>
        <div><em>Zdarzenie:</em> <InlineMath math={data.modeLatex} /></div>
        <ModeExplanation data={data} />
        <BlockMath math={aCountLine} />
      </div>

      <div>
        <strong>Krok 3 — prawdopodobieństwo</strong>
        <BlockMath math={probLine} />
      </div>
    </div>
  );
}